<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate, useLocation } from 'svelte-navigator';

  import Checked from '../assets/icons/checked.svelte';
  import {
    isFileUploadedValidationComplete,
    isPurchaseInformationValidationComplete,
    isPersonalInformationValidationComplete,
    isQuestionsValidationComplete,
    isTermsValidationComplete,
  } from '../store/stepper/steps';
  import { _ } from 'svelte-i18n';
  import Info from '../assets/icons/info.svg.svelte';
  const location = useLocation();
  let url = new URL(window.location.href);
  let step = url.pathname.split('/')[2];

  if (!step && !url.pathname.includes('admin')) {
    navigate('/step/1' + window.location.search);
  }

  let steps = [
    {
      title: 'Registration',
      description: 'Registration Description',
      active: step === '1',
      completed: step >= '1',
      link: 'step=1',
    },
    {
      title: 'Purchase Information',
      description: 'Information about the Purchase',
      active: step === '2',
      completed: step >= '2',
      link: 'step=2',
    },
    {
      title: 'Personal information',
      description: 'Personal information description',
      active: step === '3',
      completed: step >= '3',
      link: 'step=3',
    },
    {
      title: 'Questions',
      description: 'Questions to payback offer',
      active: step === '4',
      completed: step >= '4',
      link: 'step=4',
    },
    {
      title: 'Terms & Conditions',
      description: 'Terms & Conditions and next steps',
      active: step === '5',
      completed: step >= '5',
      link: 'step=5',
    },
  ];

  $: {
    if (process.env.SVELTE_APP_SKIP_PURCHASE_INFO == 'true') {
      steps = steps.filter((step) => step.link !== 'step=2');
      steps = steps;
    }
  }

  let valid = false;
  let progressPercentage: number = 0;

  switch (step) {
    case '1':
      isFileUploadedValidationComplete.subscribe((value) => {
        valid = value;
      });
      break;
    case '2':
      isPurchaseInformationValidationComplete.subscribe((value) => {
        valid = value;
      });
      break;
    case '3':
      isPersonalInformationValidationComplete.subscribe((value) => {
        valid = value;
      });
      break;
    case '4':
      isQuestionsValidationComplete.subscribe((value) => {
        valid = value;
      });
      break;
    case '5':
      isTermsValidationComplete.subscribe((value) => {
        valid = value;
      });
      break;
  }

  const updateCurrentStep = () => {
    url = new URL(window.location.href);
    step = url.pathname.split('/')[2];
    steps = steps.map((s) => {
      const stepNr = s.link.split('=')[1];
      return {
        ...s,
        active: step == stepNr,
        completed: step >= stepNr,
      };
    });
  };

  const updateProgressPercentage = () => {
    // Find the current step's position in the filtered steps array
    const currentStepIndex = steps.findIndex((s) => s.link === `step=${step}`);
    if (currentStepIndex >= 0) {
      // Progress should be based on completed steps, not current step
      progressPercentage = (currentStepIndex / steps.length) * 100;
    } else {
      // Fallback to original calculation if step not found
      progressPercentage = ((parseInt(step) - 1) / steps.length) * 100;
    }
  };

  onMount(updateCurrentStep);

  $: if ($location) updateCurrentStep();
  $: if ($location) updateProgressPercentage();
</script>

<div>
  <div class="mb-12">
    <div class="flex items-start space-x-3">
      <Info class="mt-0.5 h-5 w-5 flex-shrink-0 text-cubiq-light-blue-600" />
      <div class="flex-1">
        <p
          class="mb-2 text-sm font-medium leading-relaxed text-cubiq-dark-blue"
        >
          {$_('Complete each step to proceed with your registration')}
        </p>
        <p class="text-xs leading-relaxed text-cubiq-grey-font">
          {$_(
            'Your progress is automatically saved as you complete each section',
          )}
        </p>
      </div>
    </div>
  </div>
  <nav class="mb-12">
    <div class=" md:hidden">
      <div class="relative px-1 sm:px-2">
        <div class="flex items-start justify-between">
          {#each steps as currentStep, index}
            <div class="relative flex flex-1 flex-col items-center px-0.5 sm:px-1">
              {#if currentStep.active}
                <span
                  class="relative z-10 mb-1.5 flex h-6 w-6 items-center justify-center rounded-full border-2 bg-white sm:mb-2 sm:h-7 sm:w-7 md:h-8 md:w-8"
                  aria-current="step"
                >
                  <span
                    class="h-1.5 w-1.5 rounded-full bg-primary sm:h-2 sm:w-2 md:h-2.5 md:w-2.5"
                  />
                </span>
              {:else if currentStep.completed}
                <span
                  class="relative z-10 mb-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary sm:mb-2 sm:h-7 sm:w-7 md:h-8 md:w-8"
                >
                  <svg
                    class="h-2.5 w-2.5 text-white sm:h-3 sm:w-3 md:h-4 md:w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
              {:else}
                <span
                  class="relative z-10 mb-1.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-300 bg-white sm:mb-2 sm:h-7 sm:w-7 md:h-8 md:w-8"
                >
                  <span
                    class="h-1.5 w-1.5 rounded-full bg-transparent sm:h-2 sm:w-2 md:h-2.5 md:w-2.5"
                  />
                </span>
              {/if}
              {#if index < steps.length - 1}
                <div
                  class="absolute top-3 h-0.5 sm:top-3.5 md:top-4 {currentStep.completed
                    ? 'bg-gray-400'
                    : 'bg-gray-200'} z-0"
                  style="left: calc(50% + 1.25rem); right: calc(-50% + 1.25rem);"
                ></div>
              {/if}
              <span
                class="text-2xs text-center leading-tight sm:text-xs {currentStep.active
                  ? 'font-medium text-primary'
                  : currentStep.completed
                    ? 'text-gray-700'
                    : 'text-gray-400'} min-h-3 sm:min-h-4 md:min-h-6 mt-0.5 flex max-w-full items-center justify-center px-0.5"
              >
                <span
                  class="break-words text-center"
                  style="word-break: break-word; hyphens: auto; font-size: inherit;"
                >
                  {$_(currentStep.title)}
                </span>
              </span>
            </div>
          {/each}
        </div>
        <!-- <div class="absolute left-0 right-0 flex items-center top-5">
          {#each steps as currentStep, index}
            {#if index < steps.length - 1}
              <div class="flex items-center flex-1">
                <div
                  class="h-0.5 flex-1 {currentStep.completed &&
                  steps[index + 1].completed
                    ? 'bg-primary'
                    : currentStep.completed
                      ? 'bg-primary'
                      : 'bg-gray-200'}"
                ></div>
              </div>
            {/if}
          {/each}
        </div> -->
      </div>
    </div>

    <ol class="hidden space-y-16 md:block">
      {#each steps as step, index}
        {#if step.active}
          <li class="relative">
            {#if index !== steps.length - 1}
              <div
                class="absolute top-12 left-4 mt-0.5 h-12 w-0.5 bg-gray-300"
                aria-hidden="true"
              />
            {/if}
            <div class="group relative flex items-start">
              <span class="flex items-center justify-center">
                <span
                  class="flex h-9 w-9 items-center justify-center rounded-full border-2 border-primary bg-white"
                >
                  <span class="h-3 w-3 rounded-full bg-primary" />
                </span>
              </span>
              <span class="ml-4 flex min-w-0 flex-col">
                <span
                  class="mb-2 font-sans text-sm font-semibold font-bold tracking-wide text-primary {step.title ===
                  'Terms & Conditions'
                    ? ''
                    : 'uppercase'}"
                >
                  {$_(step.title)}
                </span>
                <span class="font-sans text-sm leading-relaxed text-gray-500">
                  {$_(step.description)}
                </span>
              </span>
            </div>
          </li>
        {:else if step.completed}
          <li class="relative">
            {#if index !== steps.length - 1}
              <div
                class="absolute top-12 left-4 mt-0.5 h-12 w-0.5 bg-gray-300"
                aria-hidden="true"
              />
            {/if}
            <div class="group relative flex items-start">
              <span class="flex items-center justify-center">
                <span
                  class="flex h-9 w-9 items-center justify-center rounded-full bg-primary transition-colors group-hover:bg-primary-800"
                >
                  <Checked />
                </span>
              </span>
              <span class="ml-4 flex min-w-0 flex-col">
                <span
                  class="mb-2 font-sans text-sm font-semibold font-bold tracking-wide text-gray-800 {step.title ===
                  'Terms & Conditions'
                    ? ''
                    : 'uppercase'}"
                >
                  {$_(step.title)}
                </span>
                <span class="font-sans text-sm leading-relaxed text-gray-500">
                  {$_(step.description)}
                </span>
              </span>
            </div>
          </li>
        {:else}
          <li class="relative">
            {#if index !== steps.length - 1}
              <div
                class="absolute top-12 left-4 mt-0.5 h-12 w-0.5 bg-gray-300"
                aria-hidden="true"
              />
            {/if}
            <div class="group relative flex items-start">
              <span class="flex items-center justify-center">
                <span
                  class="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-300 bg-white transition-colors group-hover:border-gray-400"
                >
                  <span
                    class="h-3 w-3 rounded-full bg-transparent transition-colors group-hover:bg-gray-300"
                  />
                </span>
              </span>
              <span class="ml-4 flex min-w-0 flex-col">
                <span
                  class="mb-2 font-sans text-sm font-semibold font-bold tracking-wide text-gray-500 {step.title ===
                  'Terms & Conditions'
                    ? ''
                    : 'uppercase'}"
                >
                  {$_(step.title)}
                </span>
                <span class="font-sans text-sm leading-relaxed text-gray-500">
                  {$_(step.description)}
                </span>
              </span>
            </div>
          </li>
        {/if}
      {/each}
    </ol>
  </nav>

  <div class="mt-8">
    <div class="relative mb-6">
      <div class="h-2 w-full rounded-full bg-cubiq-grey-200">
        <div
          class="h-2 rounded-full bg-gradient-to-r from-cubiq-dark-blue to-cubiq-light-blue transition-all duration-300 ease-out"
          style="width: {progressPercentage}%"
        ></div>
      </div>
    </div>

    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-3">
        <span
          class="font-sans text-sm font-semibold font-bold text-cubiq-dark-blue"
        >
          {$_('Step')}
          {steps.findIndex((s) => s.link === `step=${step}`) + 1}
          {$_('of')}
          {steps.length}
        </span>
        <div class="h-1 w-1 rounded-full bg-cubiq-grey-400"></div>
        <span class="font-sans text-sm text-cubiq-grey-font">
          {Math.round(progressPercentage)}% {$_('Complete')}
        </span>
      </div>
    </div>
  </div>
</div>

<style>
  .text-2xs {
    font-size: 0.55rem;
    line-height: 0.65rem;
  }

  @media (min-width: 350px) {
    .text-2xs {
      font-size: 0.6rem;
      line-height: 0.7rem;
    }
  }

  @media (min-width: 375px) {
    .text-2xs {
      font-size: 0.65rem;
      line-height: 0.75rem;
    }
  }
</style>
